import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../../firebase"; 
// Import updateDoc to handle editing
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

function PartyProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for the Edit Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [newImageBase64, setNewImageBase64] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'PartyProducts'));
        const productsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productsList);
      } catch (err) {
        console.error("Error fetching products: ", err);
        setError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete the product "${name}"?`)) {
      try {
        await deleteDoc(doc(db, 'PartyProducts', id));
        setProducts(prevProducts => prevProducts.filter(product => product.id !== id));
        alert('Product deleted successfully!');
      } catch (err) {
        console.error("Error deleting product: ", err);
        alert('Failed to delete product.');
      }
    }
  };
  
  // --- EDIT FUNCTIONS ---

  // Opens the modal and sets the product to be edited
  const handleEdit = (product) => {
    setCurrentProduct(product);
    setNewImageBase64(''); // Clear any previously selected new image
    setIsModalOpen(true);
  };

  // Handles changes in the modal's form fields
  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({ ...prev, [name]: value }));
  };

  // Handles a new image selection in the modal
  const handleModalImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 750 * 1024) {
      alert('File is too large. Please select an image under 750KB.');
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setNewImageBase64(reader.result);
    reader.onerror = () => alert("Failed to read the image file.");
  };
  
  // Submits the updated data to Firestore
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentProduct) return;

    try {
      const productRef = doc(db, 'PartyProducts', currentProduct.id);
      
      const updatedData = {
        ...currentProduct,
        price: parseFloat(currentProduct.price),
        quantity: parseInt(currentProduct.quantity, 10),
      };

      // If a new image was uploaded, include it in the update
      if (newImageBase64) {
        updatedData.imageUrl = newImageBase64;
      }

      await updateDoc(productRef, updatedData);

      // Update the state locally for an instant UI change
      setProducts(prevProducts => 
        prevProducts.map(p => p.id === currentProduct.id ? updatedData : p)
      );

      setIsModalOpen(false);
      alert('Product updated successfully!');
    } catch (err) {
      console.error("Error updating product: ", err);
      alert('Failed to update product.');
    }
  };

  if (loading) return <div style={styles.centered}>Loading products...</div>;
  if (error) return <div style={{ ...styles.centered, color: 'red' }}>{error}</div>;

  return (
    <div style={styles.container}>
      {/* --- PRODUCT LIST TABLE --- */}
      <div style={styles.header}>
        <h2>Party Products</h2>
        <button style={styles.addButton} onClick={() => navigate('/partyproduct/add')}>
          <FaPlus style={{ marginRight: '8px' }} />
          Add Product
        </button>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Image</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Price</th>
            <th style={styles.th}>Quantity</th>
            <th style={styles.th}>Unit</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.length > 0 ? (
            products.map(product => (
              <tr key={product.id}>
                <td style={styles.td}><img src={product.imageUrl} alt={product.name} style={styles.image} /></td>
                <td style={styles.td}>{product.name}</td>
                <td style={styles.td}>${product.price ? product.price.toFixed(2) : '0.00'}</td>
                <td style={styles.td}>{product.quantity}</td>
                <td style={styles.td}>{product.unit}</td>
                <td style={styles.td}>{product.category}</td>
                <td style={styles.td}>
                  {/* Pass the entire product object to handleEdit */}
                  <button style={{...styles.actionButton, ...styles.editButton}} onClick={() => handleEdit(product)}>
                    <FaEdit />
                  </button>
                  <button style={{...styles.actionButton, ...styles.deleteButton}} onClick={() => handleDelete(product.id, product.name)}>
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="7" style={styles.centered}>No products found.</td></tr>
          )}
        </tbody>
      </table>

      {/* --- EDIT MODAL --- */}
      {isModalOpen && currentProduct && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2>Edit Product</h2>
            <form onSubmit={handleUpdate}>
              <div style={styles.formGroup}>
                <label>Name</label>
                <input type="text" name="name" value={currentProduct.name} onChange={handleModalChange} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label>Price</label>
                <input type="number" name="price" value={currentProduct.price} onChange={handleModalChange} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label>Quantity</label>
                <input type="number" name="quantity" value={currentProduct.quantity} onChange={handleModalChange} style={styles.input} />
              </div>
               <div style={styles.formGroup}>
                <label>Unit</label>
                <input type="text" name="unit" value={currentProduct.unit} onChange={handleModalChange} style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label>Image</label>
                <img src={newImageBase64 || currentProduct.imageUrl} alt="Current" style={{width: 100, height: 100, objectFit: 'cover'}}/>
                <input type="file" accept="image/*" onChange={handleModalImageChange} style={{marginTop: '10px'}}/>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.saveButton}>Save Changes</button>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.cancelButton}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- STYLES ---
// (Includes additional styles for the modal)
const styles = {
  container: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  addButton: { display: 'flex', alignItems: 'center', padding: '10px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  th: { backgroundColor: '#f8f9fa', padding: '12px', border: '1px solid #ddd', textAlign: 'left' },
  td: { padding: '12px', border: '1px solid #ddd', verticalAlign: 'middle' },
  image: { width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' },
  actionButton: { border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', color: 'white', marginRight: '5px' },
  editButton: { backgroundColor: '#28a745' },
  deleteButton: { backgroundColor: '#dc3545' },
  centered: { textAlign: 'center', padding: '20px', fontSize: '18px' },
  // Modal Styles
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  modalContent: { backgroundColor: 'white', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '500px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' },
  formGroup: { marginBottom: '1rem' },
  input: { width: '100%', padding: '10px', boxSizing: 'border-box', borderRadius: '5px', border: '1px solid #ccc' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' },
  saveButton: { backgroundColor: '#28a745', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' },
};

export default PartyProductList;